import { forwardRef } from "react";
import styled, { css } from "styled-components";

import type { ChangeEvent, InputHTMLAttributes } from "react";



export type CheckBoxProps = {
	checked?: boolean;
	disabled?: boolean;
	label?: string;
	onChange?: (params: { checked: boolean; event: ChangeEvent<HTMLInputElement> }) => void;
	size?: "small" | "medium" | "large";
	variant?: "default" | "success" | "error" | "warning";
} & Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "size">;



/**
 * CheckBox Component
 * 
 * A customizable checkbox component with support for different sizes, variants, and states.
 * Provides visual feedback for checked, disabled, and error states.
 * 
 * @param {CheckBoxProps} props - The props for the checkbox component
 * @param {boolean} props.checked - Whether the checkbox is checked
 * @param {boolean} props.disabled - Whether the checkbox is disabled
 * @param {string} props.label - Optional label text to display next to the checkbox
 * @param {function} props.onChange - Callback function called when checkbox state changes
 * @param {"small" | "medium" | "large"} props.size - Size variant of the checkbox
 * @param {"default" | "success" | "error" | "warning"} props.variant - Visual variant of the checkbox
 * @returns {JSX.Element} The rendered checkbox component
 */
export const CheckBox = forwardRef<HTMLInputElement, CheckBoxProps>(({
	checked = false,
	disabled = false,
	label,
	onChange,
	size = "medium",
	variant = "default",
	className,
	...rest
}, ref) => {
	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (disabled) return;
		
		onChange?.({
			checked: event.target.checked,
			event,
		});
	};

	return (
		<CheckBoxContainer className={className} $disabled={disabled}>
			<StyledCheckBox
				ref={ref}
				type="checkbox"
				checked={checked}
				disabled={disabled}
				onChange={handleChange}
				$size={size}
				$variant={variant}
				{...rest}
			/>
			<CheckBoxIndicator
				$checked={checked}
				$disabled={disabled}
				$size={size}
				$variant={variant}
			>
				{checked && (
					<CheckMark $size={size}>
						<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
							<title>Tick</title>
							<path
								d="M13.5 4.5L6 12L2.5 8.5"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</CheckMark>
				)}
			</CheckBoxIndicator>
			{label && (
				<CheckBoxLabel $disabled={disabled} $size={size}>
					{label}
				</CheckBoxLabel>
			)}
		</CheckBoxContainer>
	);
});

CheckBox.displayName = "CheckBox";

const CheckBoxContainer = styled.label<{ $disabled: boolean }>`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
	user-select: none;
`;

const StyledCheckBox = styled.input<{
	$size: "small" | "medium" | "large";
	$variant: "default" | "success" | "error" | "warning";
}>`
	position: absolute;
	opacity: 0;
	width: 0;
	height: 0;
`;

const CheckBoxIndicator = styled.div<{
	$checked: boolean;
	$disabled: boolean;
	$size: "small" | "medium" | "large";
	$variant: "default" | "success" | "error" | "warning";
}>`
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: 4px;
	border: 2px solid;
	transition: all 0.2s ease-in-out;
	
	${({ $size }) => {
		switch ($size) {
			case "small":
				return css`
					width: 16px;
					height: 16px;
				`;
			case "large":
				return css`
					width: 24px;
					height: 24px;
				`;
			default:
				return css`
					width: 20px;
					height: 20px;
				`;
		}
	}}

	${({ $variant, $checked, $disabled }) => {
		if ($disabled) {
			return css`
				border-color: var(--border-disabled, #e0e0e0);
				background-color: var(--background-disabled, #f5f5f5);
				color: var(--text-disabled, #9e9e9e);
			`;
		}

		if ($checked) {
			switch ($variant) {
				case "success":
					return css`
						border-color: var(--system-success, #22c55e);
						background-color: var(--system-success, #22c55e);
						color: white;
					`;
				case "error":
					return css`
						border-color: var(--system-error, #ef4444);
						background-color: var(--system-error, #ef4444);
						color: white;
					`;
				case "warning":
					return css`
						border-color: var(--system-warning, #f59e0b);
						background-color: var(--system-warning, #f59e0b);
						color: white;
					`;
				default:
					return css`
						border-color: var(--primary, #3b82f6);
						background-color: var(--primary, #3b82f6);
						color: white;
					`;
			}
		}

		switch ($variant) {
			case "success":
				return css`
					border-color: var(--system-success, #22c55e);
					background-color: transparent;
				`;
			case "error":
				return css`
					border-color: var(--system-error, #ef4444);
					background-color: transparent;
				`;
			case "warning":
				return css`
					border-color: var(--system-warning, #f59e0b);
					background-color: transparent;
				`;
			default:
				return css`
					border-color: var(--border-input, #d1d5db);
					background-color: transparent;

					&:hover {
						border-color: var(--primary, #3b82f6);
					}
				`;
		}
	}}
`;

const CheckMark = styled.div<{ $size: "small" | "medium" | "large" }>`
	display: flex;
	align-items: center;
	justify-content: center;
	
	svg {
		${({ $size }) => {
			switch ($size) {
				case "small":
					return css`
						width: 10px;
						height: 10px;
					`;
				case "large":
					return css`
						width: 16px;
						height: 16px;
					`;
				default:
					return css`
						width: 12px;
						height: 12px;
					`;
			}
		}}
	}
`;

const CheckBoxLabel = styled.span<{
	$disabled: boolean;
	$size: "small" | "medium" | "large";
}>`
	font-family: "Inter", sans-serif;
	color: ${({ $disabled }) => 
		$disabled ? "var(--text-disabled, #9e9e9e)" : "var(--text-primary, #1f2937)"};
	
	${({ $size }) => {
		switch ($size) {
			case "small":
				return css`
					font-size: 14px;
					line-height: 20px;
				`;
			case "large":
				return css`
					font-size: 18px;
					line-height: 24px;
				`;
			default:
				return css`
					font-size: 16px;
					line-height: 22px;
				`;
		}
	}}
`;
